import { useCallback, useEffect, useMemo, useState } from 'react';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { TargetImageInfo } from './types';
import { NEW_SS_SENTINEL } from './constants';

export function useTargetImageSelection(
    patient: Patient,
    sourceImage: Image3D,
    sourceStructureSet: StructureSetReadModel
) {
    const [selectedImageSeriesUid, setSelectedImageSeriesUid] = useState<
        string | null
    >(null);
    const [selectedRegistrationUid, setSelectedRegistrationUid] = useState<
        string | null
    >(null);
    const [selectedImageSsUid, setSelectedImageSsUid] =
        useState<string>(NEW_SS_SENTINEL);

    const targetImages: TargetImageInfo[] = useMemo(() => {
        return patient.images
            .map((img) => ({
                image: img,
                isSourceImage: img.seriesUid === sourceImage.seriesUid,
                geometryMatch: sourceImage.grid.hasEqualGeometryTo(img.grid),
                sameFrameOfReference:
                    img.seriesUid !== sourceImage.seriesUid &&
                    !sourceImage.grid.hasEqualGeometryTo(img.grid) &&
                    img.frameOfReferenceUid === sourceImage.frameOfReferenceUid,
                registrations: patient.findRegistrationsBetween(
                    sourceImage,
                    img
                ),
                structureSets: patient.structureSets
                    .filter((ss) => {
                        if (ss.image?.seriesUid !== img.seriesUid) {
                            return false;
                        }
                        if (
                            ss.dicomStructureSet.sopInstanceUid ===
                            sourceStructureSet.dicomUid
                        ) {
                            return false;
                        }
                        return true;
                    })
                    .map((ss) => ({
                        structureSet: ss,
                        label: ss.structureSetLabel,
                    })),
            }))
            .sort((a, b) => {
                if (a.isSourceImage !== b.isSourceImage) {
                    return a.isSourceImage ? -1 : 1;
                }
                const scoreA = a.geometryMatch
                    ? 2
                    : a.sameFrameOfReference
                      ? 1.5
                      : a.registrations.length > 0
                        ? 1
                        : 0;
                const scoreB = b.geometryMatch
                    ? 2
                    : b.sameFrameOfReference
                      ? 1.5
                      : b.registrations.length > 0
                        ? 1
                        : 0;
                if (scoreA !== scoreB) {
                    return scoreB - scoreA;
                }
                return (
                    a.image.seriesMetadata.seriesDescription || ''
                ).localeCompare(b.image.seriesMetadata.seriesDescription || '');
            });
    }, [patient, sourceImage, sourceStructureSet.dicomUid]);

    const selectedTarget = useMemo(
        () =>
            targetImages.find(
                (t) => t.image.seriesUid === selectedImageSeriesUid
            ) ?? null,
        [targetImages, selectedImageSeriesUid]
    );

    const availableRegistrations = useMemo(() => {
        if (!selectedTarget) {
            return [];
        }
        if (
            selectedTarget.isSourceImage ||
            selectedTarget.geometryMatch ||
            selectedTarget.sameFrameOfReference
        ) {
            return [];
        }
        return selectedTarget.registrations;
    }, [selectedTarget]);

    const activeRegistration = useMemo(() => {
        if (availableRegistrations.length === 0) {
            return null;
        }
        if (selectedRegistrationUid) {
            return (
                availableRegistrations.find(
                    (r) => r.sopInstanceUid === selectedRegistrationUid
                ) ?? availableRegistrations[0]
            );
        }
        return availableRegistrations[0];
    }, [availableRegistrations, selectedRegistrationUid]);

    const activeImageTargetSs = useMemo(() => {
        if (!selectedTarget || selectedImageSsUid === NEW_SS_SENTINEL) {
            return null;
        }
        return (
            selectedTarget.structureSets.find(
                (s) =>
                    s.structureSet.dicomStructureSet.sopInstanceUid ===
                    selectedImageSsUid
            )?.structureSet ?? null
        );
    }, [selectedTarget, selectedImageSsUid]);

    // Auto-select first image if none selected
    useEffect(() => {
        if (targetImages.length > 0 && !selectedImageSeriesUid) {
            setSelectedImageSeriesUid(targetImages[0].image.seriesUid);
        }
    }, [targetImages, selectedImageSeriesUid]);

    // Auto-select registration when target changes
    useEffect(() => {
        if (!selectedTarget) {
            return;
        }

        if (selectedTarget.registrations.length > 0) {
            setSelectedRegistrationUid(
                selectedTarget.registrations[0].sopInstanceUid
            );
        } else {
            setSelectedRegistrationUid(null);
        }
    }, [selectedTarget]);

    const selectImage = useCallback((seriesUid: string) => {
        setSelectedImageSeriesUid(seriesUid);
        // Reset to "Create new structure set" when user explicitly selects an image
        setSelectedImageSsUid(NEW_SS_SENTINEL);
    }, []);

    const selectStructureSet = useCallback((ssUid: string) => {
        setSelectedImageSsUid(ssUid);
    }, []);

    const selectRegistration = useCallback((regUid: string) => {
        setSelectedRegistrationUid(regUid);
    }, []);

    return {
        targetImages,
        selectedTarget,
        availableRegistrations,
        activeRegistration,
        activeImageTargetSs,
        selectedImageSeriesUid,
        selectedRegistrationUid,
        selectedImageSsUid,
        selectImage,
        selectStructureSet,
        selectRegistration,
    };
}
